import { Admin, Authenticated, CustomRoutes } from 'react-admin';
import { authProvider } from './authProvider';
import { Navigate, Route } from 'react-router';
import { ForgotPasswordPage } from './customRoutes/authentication/ForgotPasswordPage';
import { CompleteProfilePage } from './customRoutes/authentication/CompleteProfilePage';
import AuthPageWrapper from './customRoutes/authentication/components/AuthPageWrapper';
import Dashboard from './dashboard/Dashboard';
import NotFound from './NotFound';
import { QueryClient } from '@tanstack/react-query';
import { getDataProvider } from './dataProvider/dataProvider';
import { useEffect, useState } from 'react';
import { i18nProvider } from './i18nProvider';
import ReportingPage from './reporting/ReportingPage';
import { OfferList } from './resources/offers/list/OfferList';
import { OfferShow } from './resources/offers/show/OfferShow';
import { PublisherLayout } from './layout/publisherLayout/PublisherLayout';
import NavigatePublisherIdPrefix from './components/NavigatePublisherIdPrefix';
import AuthActionPage from './customRoutes/authentication/action/AuthActionPage';
import { Layout, LoginPage, Error, ThemeProvider } from '@/components/admin';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, gcTime: 0 },
  },
});

// react-admin's <AdminUI> always renders a notification component (defaulting to
// its MUI Snackbar). We surface notifications through the sonner-based
// <Notification> in the custom Layout instead, so disable the built-in one to
// avoid every toast showing twice.
const NoNotification = () => null;

export const App = () => {
  const [dataProvider, setDataProvider] = useState<any>();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getDataProvider().then(d => {
      setDataProvider(d);
    });
    authProvider.setBrowserLocalPersistence().then(() => setReady(true));
  }, []);

  if (!dataProvider) return null;
  if (!ready) return null;

  return (
    <ThemeProvider defaultTheme="dark">
      <Admin
        requireAuth
        queryClient={queryClient}
        dataProvider={dataProvider}
        authProvider={authProvider}
        loginPage={LoginPage}
        dashboard={Dashboard}
        layout={Layout}
        notification={NoNotification}
        i18nProvider={i18nProvider}
        catchAll={NotFound}
        error={Error}
      >
        <CustomRoutes>
          <Route
            path="/offers/*"
            element={<NavigatePublisherIdPrefix replace />}
          />
          <Route path="/publisher/:publisherId" element={<PublisherLayout />}>
            {/*Offers*/}
            <Route path="" element={<NavigatePublisherIdPrefix replace />} />
            <Route path="offers/:status" element={<OfferList />} />
            <Route path="offers/show/:offerId" element={<OfferShow />} />
            <Route
              path="offers"
              element={<NavigatePublisherIdPrefix replace />}
            />

            {/*Reporting*/}
            <Route path="performance" element={<ReportingPage />} />
          </Route>
        </CustomRoutes>

        {/* Auth Routes */}
        <CustomRoutes noLayout>
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route
            path="/forgot-password"
            element={
              <AuthPageWrapper>
                <ForgotPasswordPage />
              </AuthPageWrapper>
            }
          />
          <Route
            path="auth/action"
            element={
              <AuthPageWrapper>
                <AuthActionPage />
              </AuthPageWrapper>
            }
          />
          <Route
            path="/complete-profile"
            element={
              <Authenticated>
                <AuthPageWrapper>
                  <CompleteProfilePage />
                </AuthPageWrapper>
              </Authenticated>
            }
          />
        </CustomRoutes>
      </Admin>
    </ThemeProvider>
  );
};
